import React, { useMemo, useState } from "react";
import { Loader2, X, AlertTriangle } from "lucide-react";
import type {
  CarouselCardSnapshot,
  CarouselPublicationDoc,
} from "src/types/carouselPublication";
import { DateTimePicker } from "src/components/ui/DateTimePicker";
import { SlabButton } from "src/components/ui/SlabButton";
import { Constants } from "src/constant";

interface RepublishDialogProps {
  publication: CarouselPublicationDoc;
  /** Number of cards currently in the draft — surfaced so the operator knows what gets cleared. */
  currentDraftCount: number;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (params: {
    includeCardIds: string[];
    newExpiresAtLocal: string;
  }) => Promise<void>;
}

/**
 * Default new expiry: 7 days out at 11:59 PM local. Covers the common "weekly
 * campaign" case while still being short enough that the operator usually wants
 * to override it.
 */
function defaultNewExpiresAtLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(23, 59, 0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/**
 * Confirm dialog for restoring a past carousel into the working draft.
 *
 * - Per-card include checkboxes (defaults to "all in").
 * - Single new expiry that applies to all restored cards (almost always wanted
 *   because original snapshot expiry has long since passed).
 * - Clear callout about *replacing* the current draft.
 *
 * Note: clicking confirm restores into draft only — the operator still has to
 * click "Publish carousel" to push the restored set live. That's intentional so
 * they can tweak before going live.
 */
export const RepublishDialog: React.FC<RepublishDialogProps> = ({
  publication,
  currentDraftCount,
  submitting,
  error,
  onClose,
  onConfirm,
}) => {
  const orderedCards = useMemo<CarouselCardSnapshot[]>(
    () => [...publication.cards].sort((a, b) => a.order - b.order),
    [publication],
  );

  const [included, setIncluded] = useState<Set<string>>(
    () => new Set(orderedCards.map((c) => c.id)),
  );
  const [newExpiresAtLocal, setNewExpiresAtLocal] = useState<string>(
    defaultNewExpiresAtLocal(),
  );

  const includeCount = included.size;

  const toggle = (id: string) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (includeCount === 0) return;
    await onConfirm({
      includeCardIds: Array.from(included),
      newExpiresAtLocal,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="republish-dialog-title"
      className="fixed inset-0 z-[300] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <div
        className="relative max-h-full w-full overflow-y-auto rounded-t-[2rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:max-w-3xl sm:rounded-[2rem] sm:p-8"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2
              id="republish-dialog-title"
              className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl"
            >
              Restore this carousel to draft
            </h2>
            <p className="text-sm font-bold text-[#777777]">
              {Constants.HAPPENING_NOW_REPUBLISH_DESCRIPTION}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
            className="rounded-xl p-2 text-[#777777] hover:bg-[#F7F7F7] hover:text-[#3C3C3C] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {currentDraftCount > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border-2 border-[#F59E0B] bg-[#FEF3C7] p-3 text-[#92400E]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <p className="text-xs font-extrabold sm:text-sm">
              Your current draft has {currentDraftCount}{" "}
              {currentDraftCount === 1 ? "card" : "cards"} — those will be
              cleared. (The live carousel won't change until you click Publish.)
            </p>
          </div>
        )}

        {error && (
          <p className="mb-4 rounded-xl bg-[#FEE2E2] px-4 py-3 text-center text-sm font-extrabold text-[#B91C1C]">
            {error}
          </p>
        )}

        <div className="mb-4">
          <label className="flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]">
            {Constants.REPUBLISH_EXPIRY_DESCRIPTION}
            <DateTimePicker
              accent="teal"
              value={newExpiresAtLocal}
              onChanged={({ value }) => setNewExpiresAtLocal(value)}
              required
              aria-label="New expiry date and time for restored cards"
            />
          </label>
        </div>

        <div className="mb-4">
          <p className="mb-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]">
            Cards ({includeCount} of {orderedCards.length} selected)
          </p>
          <ul className="flex flex-col gap-2">
            {orderedCards.map((card) => {
              const isIn = included.has(card.id);
              return (
                <li
                  key={card.id}
                  className={[
                    "flex items-center gap-3 rounded-2xl border-2 bg-white p-2",
                    isIn ? "border-[#14B8A6]" : "border-[#E5E5E5]",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={isIn}
                    onChange={() => toggle(card.id)}
                    disabled={submitting}
                    aria-label={`Include ${card.title}`}
                    className="h-5 w-5 accent-[#14B8A6]"
                  />
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border-2 border-[#E5E5E5] bg-[#F7F7F7]">
                    <img
                      src={card.imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-extrabold text-[#3C3C3C]">
                      {card.title}
                    </p>
                    <p className="truncate font-mono text-[11px] font-bold text-[#6366F1]">
                      {card.targetLink}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border-2 border-[#E5E5E5] bg-white px-5 text-sm font-extrabold text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0] disabled:opacity-50"
          >
            Cancel
          </button>
          <SlabButton
            type="button"
            variant="primary"
            disabled={submitting || includeCount === 0}
            onClick={handleConfirm}
            backgroundColor="#6366F1"
            lipColor="#4338CA"
            shadowLiftPx={5}
            shadowLiftActivePx={2}
            className="min-h-[48px] rounded-2xl px-6 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Restoring…
              </>
            ) : (
              `Restore ${includeCount} ${
                includeCount === 1 ? "card" : "cards"
              } to draft`
            )}
          </SlabButton>
        </div>
      </div>
    </div>
  );
};
